import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction } from "react"
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline"
import { Table } from "@/components/general/Table"
import { SmallTruckRatesType } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"
import { VehicleCard } from "../user/VehicleCard"
import Swal from "sweetalert2"
import { convertEquipmentType } from "@/lib/services"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { smallTruckRate: SmallTruckRatesType[] }
  header: {
    name: string
    width: string
  }[]
  handleForm: (form: SmallTruckRatesType) => void
  handleFormUpdate: (form: SmallTruckRatesType) => void
  handleFormDelete: (form: string) => void
  title: string
  setTitle: (title: string) => void
}

export const SmallTruckRates: FC<Props> = (props) => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<SmallTruckRatesType>()

  const onSubmit = (data: SmallTruckRatesType) => {
    if (data.id == undefined) {
      props.handleForm(data)
    } else {
      props.handleFormUpdate(data)
    }
  }

  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      // color: "#ffffff",
      // background: "#333333",
      showCancelButton: true,
      confirmButtonColor: "#CEA16B",
      cancelButtonColor: "#a33333",
      customClass: {
        popup: 'custom-popup',
      },
      confirmButtonText: "<p style='color: black;'>Yes, Delete it!</p>",
    }).then(async (result) => {
      if (result.isConfirmed) {
        props.handleFormDelete(id)
        Swal.fire({
          icon: "success",
          title: "Record Deleted",
          // background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          }
        })
      }
    })
  }

  const populateData = (e: SmallTruckRatesType) => {
    props.setTitle('Update Small Truck Rate')
    setValue("id", e.id)
    setValue("equipmentType", e.equipmentType)
    setValue("groupName", e.groupName)
    setValue("milesMin", e.milesMin)
    setValue("milesMax", e.milesMax)
    setValue("ratePerMile", e.ratePerMile)
    setValue("minRate", e.minRate)
    setValue("maxRate", e.maxRate)
    props.setPopup(true)
  }

  useResetOnClose(props.popup, () => {
    reset({
      equipmentType: "",
      groupName: "",
      milesMin: undefined,
      milesMax: undefined,
      ratePerMile: undefined,
      minRate: undefined,
      maxRate: undefined,
      id: undefined,
    })
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="small"
        title = {props.title}
      >
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-1">
              <input
                className="hidden vehicle_radio_customize "
                type="radio"
                id="vehicle_card3"
                value="STRAIGHT_TRUCK"
                {...register("equipmentType", { required: true })}
              />
              <label htmlFor="vehicle_card3">
                <VehicleCard
                  image="/images/straight-truck.svg"
                  name="Straight Truck"
                />
              </label>
            </div>
            <div className="col-span-1">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card4"
                value="SPRINTER_VAN"
                {...register("equipmentType", { required: true })}
              />
              <label htmlFor="vehicle_card4">
                <VehicleCard image="/images/sprinter.svg" name="Sprinter" />
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Dat Low Mod</label>
              <input
                className={
                  (errors.groupName ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                type="text"
                placeholder="Enter Dat Low Mod"
                {...register("groupName", { required: true })}
              />
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Miles Min</label>
              <input
                className={
                  (errors.milesMin ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Miles Min"
                {...register("milesMin", { required: true })}
              />
            </fieldset>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Miles Max</label>
              <input
                className={
                  (errors.milesMax ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Miles Max"
                {...register("milesMax", { required: true })}
              />
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Rate Per Mile</label>
              <input
                className={
                  (errors.milesMax ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Rate Per Mile"
                {...register("ratePerMile", { required: true })}
              />
            </fieldset>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Min Rate</label>
              <input
                className={
                  (errors.minRate ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Min Rate"
                {...register("minRate", { required: true })}
              />
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Max Rate</label>
              <input
                className={
                  (errors.maxRate ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 outline-none dark:text-white border-2 rounded placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Max Rate"
                {...register("maxRate", { required: true })}
              />
            </fieldset>
          </div>
          <button
            onClick={handleSubmit(onSubmit)}
            className=" bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-secondary-1 text-primary-1"
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.smallTruckRate.map((e, i) => (
            <tr key={i} className="text-left">
              <td className={"whitespace-nowrap p-4 w-40"}>
                {convertEquipmentType(e.equipmentType)}
              </td>
              <td className={"whitespace-nowrap p-4 w-40"}>{e.groupName}</td>
              <td className={"whitespace-nowrap p-4 w-40"}>{e.milesMin}</td>
              <td className={"whitespace-nowrap p-4 w-40"}>{e.milesMax}</td>
              <td className={"whitespace-nowrap p-4 w-40"}>{e.ratePerMile}</td>
              <td className={"whitespace-nowrap p-4 w-40"}>{e.minRate}</td>
              <td className={"whitespace-nowrap p-4 w-40"}>
                <p>{e.maxRate}</p>
              </td>
              <td className={"whitespace-nowrap p-4 w-24"}>
                <div className="flex justify-end gap-2">
                  <PencilSquareIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => populateData(e)}
                  />
                  <TrashIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => onDelete(e.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
